import { useState, useEffect } from "react";
import API from "../lib/api";
import { toast } from "sonner";
import { BellRinging } from "@phosphor-icons/react";

const PREFS = [
  { key: "morning_briefing", label: "Morning briefing", description: "Daily 06:30 email with the jobs scheduled for today." },
  { key: "evening_reminder", label: "Tomorrow heads-up", description: "Evening 18:00 email with the jobs scheduled for tomorrow." },
];

export default function NotificationPreferencesCard({ apiBasePath = "/in-house-portal" }) {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    API.get(`${apiBasePath}/notification-preferences`)
      .then(({ data }) => setPrefs(data.notification_preferences))
      .catch(() => setPrefs({}));
  }, [apiBasePath]);

  const toggle = async (key) => {
    if (!prefs) return;
    const next = { ...prefs, [key]: !prefs[key] };
    setSaving(key);
    setPrefs(next);
    try {
      const { data } = await API.put(`${apiBasePath}/notification-preferences`, {
        notification_preferences: { [key]: next[key] },
      });
      setPrefs(data.notification_preferences);
      toast.success(next[key] ? "You'll receive this email again." : "Email turned off.");
    } catch {
      setPrefs(prefs);
      toast.error("Could not update preference. Try again.");
    } finally {
      setSaving(null);
    }
  };

  if (!prefs) return null;

  return (
    <div className="border border-border bg-card rounded-lg p-4" data-testid="notification-preferences">
      <div className="flex items-center gap-2 mb-3">
        <BellRinging size={18} className="text-slate-500" />
        <h2 className="text-base font-semibold">Notification preferences</h2>
      </div>
      <div className="space-y-2">
        {PREFS.map((p) => {
          const on = prefs[p.key] !== false;
          return (
            <label
              key={p.key}
              className="flex items-start justify-between gap-3 rounded-md border border-border px-3 py-2 cursor-pointer hover:bg-muted/40"
              data-testid={`notif-pref-${p.key}`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{p.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{p.description}</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={on}
                disabled={saving === p.key}
                onClick={() => toggle(p.key)}
                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition ${on ? "bg-primary" : "bg-slate-300 dark:bg-slate-600"} ${saving === p.key ? "opacity-50" : ""}`}
                data-testid={`notif-toggle-${p.key}`}
              >
                <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition ${on ? "translate-x-4" : "translate-x-0.5"}`} />
              </button>
            </label>
          );
        })}
      </div>
    </div>
  );
}
